import buildHeaders from './fetchHeaders';
import {API_BASE} from './reports';

const ICR_DRAFT_TIMEOUT_MS = 90 * 1000;
const ICR_DRAFT_MAX_RETRIES = 2;
const RETRY_DELAY_MS = 250;
const ICR_DRAFT_MODEL = process.env.REACT_APP_ICR_LLM_MODEL || process.env.REACT_APP_RFI_LLM_MODEL || 'gpt-5.2';
const ICR_DRAFT_FALLBACK_MODELS = ['gpt-4o-mini'];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithTimeout = async (url, options = {}, timeoutMs = ICR_DRAFT_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(url, {...options, signal: controller.signal});
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error?.name === 'AbortError') {
            throw new Error(`ICR generation timed out after ${Math.round(timeoutMs / 1000)}s`);
        }
        throw error;
    }
};

const parseJsonFromText = (rawText = '') => {
    const trimmed = String(rawText || '').trim();
    if (!trimmed) {
        throw new Error('LLM returned empty content');
    }

    const withoutFence = trimmed
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/\s*```$/, '');

    try {
        return JSON.parse(withoutFence);
    } catch (_) {
        const firstBrace = withoutFence.indexOf('{');
        const lastBrace = withoutFence.lastIndexOf('}');
        if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
            throw new Error('LLM returned non-JSON content');
        }
        return JSON.parse(withoutFence.slice(firstBrace, lastBrace + 1));
    }
};

const unique = (items = []) => Array.from(new Set(items.filter(Boolean)));

const resolveIcrDraftModels = () => {
    const fromEnv = String(process.env.REACT_APP_ICR_LLM_MODEL || process.env.REACT_APP_RFI_LLM_MODEL || '').trim();
    const envModels = fromEnv ? fromEnv.split(',').map((m) => m.trim()) : [ICR_DRAFT_MODEL];
    return unique([...envModels, ...ICR_DRAFT_FALLBACK_MODELS]);
};

const isModelOrParameterError = (status, message = '') => {
    const text = String(message || '').toLowerCase();
    if (status === 400 || status === 404) {
        return (
            text.includes('invalid_request_error') ||
            text.includes('not a chat model') ||
            text.includes('unsupported parameter') ||
            (text.includes('model') && text.includes('not supported')) ||
            (text.includes('model') && text.includes('does not exist'))
        );
    }
    return false;
};

const isNonRetryableContentError = (message = '') => {
    const text = String(message || '').toLowerCase();
    return text.includes('llm returned empty content') || text.includes('llm returned non-json content');
};

const runSingleModelAttempt = async ({model, messages, reportId}) => {
    const response = await fetchWithTimeout(`${API_BASE}/llm`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({
            model,
            messages,
            options: {
                temperature: 0,
            },
            context: {
                reportId,
                task: 'pitch_to_icr',
            }
        })
    }, ICR_DRAFT_TIMEOUT_MS);

    if (!response.ok) {
        const errorText = await response.text();
        const err = new Error(`ICR generation failed (model=${model}): ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
        err.status = response.status;
        err.errorText = errorText;
        throw err;
    }

    const payload = await response.json();
    const content = payload?.choices?.[0]?.message?.content || payload?.content || payload?.result || '';
    const parsed = typeof content === 'string' ? parseJsonFromText(content) : content;
    return normalizeGeneratedDraft(parsed);
};

const normalizeGeneratedDraft = (draft = {}) => ({
    subject: draft.subject || '',
    informationRequirement: draft.informationRequirement || '',
    eeiWhat: draft.eei?.what || draft.eeiWhat || '',
    eeiWhere: draft.eei?.where || draft.eeiWhere || '',
    eeiWhen: draft.eei?.when || draft.eeiWhen || '',
    eeiDetail: draft.eei?.detail || draft.eeiDetail || '',
    backgroundJustification: draft.backgroundJustification || '',
    priority: draft.priority || 'Routine',
    classification: draft.classification || 'UNCLASSIFIED',
    requestingOrganization: draft.requestingOrganization || '',
    requestor: draft.requestor || '',
    pointOfContact: draft.pointOfContact || '',
    expirationDate: draft.expirationDate || '',
    tags: Array.isArray(draft.tags) ? draft.tags : [],
    targetNameTypeActivity: draft.targetInformation?.targetNameTypeActivity || draft.targetNameTypeActivity || '',
    geographicArea: draft.targetInformation?.geographicArea || draft.geographicArea || '',
    timeOfInterest: draft.targetInformation?.timeOfInterest || draft.timeOfInterest || '',
    recommendedCollectionDisciplines: Array.isArray(draft.recommendedCollectionDisciplines)
        ? draft.recommendedCollectionDisciplines
        : [],
    knownSourcesPreviousReporting: draft.knownSourcesPreviousReporting || '',
    constraintsSpecialInstructions: draft.constraintsSpecialInstructions || '',
    classificationCaveats: draft.classificationCaveats || '',
    disseminationControls: draft.disseminationControls || '',
    validatedBy: draft.validation?.validatedBy || draft.validatedBy || '',
    validatorPosition: draft.validation?.position || draft.validatorPosition || '',
    validationDate: draft.validation?.date || draft.validationDate || '',
    collectionManagerNotes: draft.collectionManagerNotes || '',
    responseFeedback: draft.responseFeedback || '',
});

const buildPrompt = ({pitchText, metadata, title}) => {
    const compactMeta = {
        title: title || metadata?.title || '',
        productType: metadata?.productType || metadata?.productTypeValue || 'pitch',
        authors: metadata?.authors || [],
        classification: metadata?.classification || '',
        originator: metadata?.originator || '',
        createdAt: metadata?.createdAt || null,
        updatedAt: metadata?.updatedAt || null,
        tags: metadata?.tags || [],
        summary: metadata?.summary || '',
    };

    return [
        'You are an intelligence collection planner. Generate a complete ICR draft from a pitch.',
        'Return ONLY valid JSON with this exact shape:',
        JSON.stringify({
            subject: '',
            informationRequirement: '',
            eei: {what: '', where: '', when: '', detail: ''},
            backgroundJustification: '',
            priority: 'Routine',
            classification: 'UNCLASSIFIED',
            requestingOrganization: '',
            requestor: '',
            pointOfContact: '',
            expirationDate: '',
            tags: [],
            targetInformation: {targetNameTypeActivity: '', geographicArea: '', timeOfInterest: ''},
            recommendedCollectionDisciplines: [],
            knownSourcesPreviousReporting: '',
            constraintsSpecialInstructions: '',
            classificationCaveats: '',
            disseminationControls: '',
            validation: {validatedBy: '', position: '', date: ''},
            collectionManagerNotes: '',
            responseFeedback: ''
        }),
        'Rules:',
        '- Do not include markdown fences.',
        '- If unknown, leave empty string/array.',
        '- Keep output concise and operational.',
        '- Do not prefix subject with "ICR:" or "ICR Draft:".',
        '',
        `Pitch title: ${compactMeta.title}`,
        `Report metadata: ${JSON.stringify(compactMeta)}`,
        `Pitch text:\n${pitchText}`
    ].join('\n');
};

export async function generateIcrDraftFromPitch({reportId, pitchText, metadata = {}, title = ''}) {
    const sanitizedPitch = String(pitchText || '').trim();
    if (!sanitizedPitch) {
        throw new Error('Pitch content is empty. Please add report content before creating an ICR.');
    }

    const messages = [
        {role: 'system', content: 'You produce strict JSON only.'},
        {role: 'user', content: buildPrompt({pitchText: sanitizedPitch, metadata, title})}
    ];

    const models = resolveIcrDraftModels();
    const modelErrors = [];

    for (const model of models) {
        let lastError = null;

        for (let attempt = 0; attempt <= ICR_DRAFT_MAX_RETRIES; attempt += 1) {
            try {
                return await runSingleModelAttempt({model, messages, reportId});
            } catch (error) {
                lastError = error;
                const status = Number.isFinite(error?.status) ? error.status : null;
                const errorBody = error?.errorText || error?.message || '';
                const nonRetryableForModel =
                    isModelOrParameterError(status, errorBody) ||
                    isNonRetryableContentError(errorBody);

                if (nonRetryableForModel) {
                    break;
                }

                if (attempt < ICR_DRAFT_MAX_RETRIES) {
                    await sleep(RETRY_DELAY_MS * (attempt + 1));
                }
            }
        }

        modelErrors.push({model, error: lastError});
    }

    const details = modelErrors
        .map(({model, error}) => `${model}: ${error?.message || 'unknown error'}`)
        .join(' | ');
    throw new Error(`Failed to generate ICR draft after trying models [${models.join(', ')}]. ${details}`);
}

